import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import {
  kubernetesApi,
  K8sCluster,
  K8sNode,
  K8sPod,
  K8sDeployment,
  K8sNamespace,
  K8sEvent,
  UnhealthyDeployment,
  ServiceWithoutEndpoints,
  HPAAtLimit,
  Recommendation
} from '../../services/api/kubernetes.api';

export default function KubernetesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['infrastructure', 'common']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clusters, setClusters] = useState<K8sCluster[]>([]);
  const [nodes, setNodes] = useState<K8sNode[]>([]);
  const [pods, setPods] = useState<K8sPod[]>([]);
  const [deployments, setDeployments] = useState<K8sDeployment[]>([]);
  const [namespaces, setNamespaces] = useState<K8sNamespace[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<K8sCluster | null>(null);
  const [selectedTab, setSelectedTab] = useState<'nodes' | 'pods' | 'deployments' | 'namespaces'>('nodes');
  const [resourceUsage, setResourceUsage] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // New state for insights and analytics
  const [criticalEvents, setCriticalEvents] = useState<K8sEvent[]>([]);
  const [unhealthyDeployments, setUnhealthyDeployments] = useState<UnhealthyDeployment[]>([]);
  const [servicesWithoutEndpoints, setServicesWithoutEndpoints] = useState<ServiceWithoutEndpoints[]>([]);
  const [hpasAtLimit, setHpasAtLimit] = useState<HPAAtLimit[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [eventFilter, setEventFilter] = useState<'all' | 'error' | 'warning'>('all');
  const [healthScore, setHealthScore] = useState<number>(0);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedCluster) {
      fetchClusterUtilization(selectedCluster.id);
      fetchInsightsData(selectedCluster.id);
      // Load deployments for this cluster
      kubernetesApi.getDeployments(selectedCluster.id).then(setDeployments).catch(console.error);
      setCurrentPage(1);
    }
  }, [selectedCluster]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [clustersData, nodesData, podsData, namespacesData] = await Promise.all([
        kubernetesApi.getClusters(),
        kubernetesApi.getNodes(),
        kubernetesApi.getPods(),
        kubernetesApi.getNamespaces()
      ]);
      console.log('K8s Data loaded:', {
        clusters: clustersData.length,
        nodes: nodesData.length,
        pods: podsData.length,
        namespaces: namespacesData.length
      });
      setClusters(clustersData);
      setNodes(nodesData);
      setPods(podsData);
      setNamespaces(namespacesData);
      if (clustersData.length > 0) {
        console.log('Selected cluster:', clustersData[0]);
        setSelectedCluster(clustersData[0]);
        // Load deployments for the selected cluster
        const deploymentsData = await kubernetesApi.getDeployments(clustersData[0].id);
        setDeployments(deploymentsData);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load Kubernetes data');
      console.error('Kubernetes fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClusterUtilization = async (clusterId: string) => {
    try {
      const utilData = await kubernetesApi.getResourceUtilization(clusterId);
      setResourceUsage(utilData);
    } catch (err: any) {
      console.error('Failed to fetch cluster utilization:', err);
    }
  };

  const fetchInsightsData = async (clusterId: string) => {
    try {
      // Fetch insights data from backend API
      const [insightsData, allEvents, recommendationsData] = await Promise.all([
        kubernetesApi.getClusterInsights(clusterId),
        kubernetesApi.getClusterEvents(clusterId, undefined, 50),
        kubernetesApi.getRecommendations(clusterId)
      ]);

      console.log('K8s Insights loaded:', {
        criticalEvents: insightsData.criticalEvents.length,
        unhealthyDeployments: insightsData.unhealthyDeployments.length,
        servicesWithoutEndpoints: insightsData.servicesWithoutEndpoints.length,
        hpasAtLimit: insightsData.hpasAtLimit.length,
        healthScore: insightsData.healthScore,
        recommendations: recommendationsData.length
      });

      setCriticalEvents(allEvents);
      setUnhealthyDeployments(insightsData.unhealthyDeployments);
      setServicesWithoutEndpoints(insightsData.servicesWithoutEndpoints);
      setHpasAtLimit(insightsData.hpasAtLimit);
      setRecommendations(recommendationsData);
      setHealthScore(insightsData.healthScore);
    } catch (err: any) {
      console.error('Failed to fetch insights data:', err);
      // Keep empty state on error
      setCriticalEvents([]);
      setUnhealthyDeployments([]);
      setServicesWithoutEndpoints([]);
      setHpasAtLimit([]);
      setRecommendations([]);
      setHealthScore(0);
    }
  };

  const getStatusColor = (status: string) => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case 'healthy':
      case 'ready':
      case 'running':
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'warning':
      case 'pending':
      case 'notready':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'critical':
      case 'crashloopbackoff':
      case 'failed':
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  // Helper functions for new features
  const formatTimestamp = (timestamp: string | Date) => {
    const now = new Date();
    const eventDate = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const diff = Math.floor((now.getTime() - eventDate.getTime()) / 1000 / 60); // minutes
    if (diff < 60) return `${diff}m ${t('common:timeAgo')}`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ${t('common:timeAgo')}`;
    return `${Math.floor(diff / 1440)}d ${t('common:timeAgo')}`;
  };

  const getSeverityColor = (severity: 'info' | 'error' | 'warning') => {
    if (severity === 'error') {
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    } else if (severity === 'warning') {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
    }
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
  };

  const getRecommendationColor = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return 'border-red-500 bg-red-50 dark:bg-red-900/10';
      case 'medium':
        return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/10';
      case 'low':
        return 'border-blue-500 bg-blue-50 dark:bg-blue-900/10';
      default:
        return 'border-gray-500 bg-gray-50 dark:bg-gray-900/10';
    }
  };

  const getRecommendationIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return (
          <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'medium':
        return (
          <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'low':
        return (
          <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="h-6 w-6 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  // Calculate pod status distribution for pie chart
  const getPodStatusData = () => {
    const statusCounts = filteredPods.reduce((acc, pod) => {
      const status = pod.status || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(statusCounts).map(([name, value]) => ({ name, value }));
  };

  // Calculate namespace pod counts for bar chart
  const getNamespacePodCounts = () => {
    const namespaceCounts = filteredPods.reduce((acc, pod) => {
      // Support both namespace as object or string
      const namespace = typeof pod.namespace === 'object' ? pod.namespace?.name : pod.namespace || 'default';
      acc[namespace] = (acc[namespace] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(namespaceCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  // Calculate node resource utilization
  const getNodeResourceUtilization = () => {
    return filteredNodes.map(node => ({
      name: node.name.length > 15 ? node.name.substring(0, 15) + '...' : node.name,
      cpu: node.cpu_usage_percent || 0,
      memory: node.memory_usage_percent || 0
    }));
  };

  // Get problematic pods (high restarts, failed states)
  const getProblematicPods = () => {
    return filteredPods
      .filter(pod => {
        const status = pod.status?.toLowerCase();
        return (
          pod.restart_count > 3 ||
          status === 'crashloopbackoff' ||
          status === 'failed' ||
          status === 'error' ||
          status === 'pending' ||
          status === 'imagepullbackoff'
        );
      })
      .sort((a, b) => {
        // Priority: CrashLoopBackOff > Failed > Pending > High Restarts
        const getPriority = (pod: K8sPod) => {
          const status = pod.status?.toLowerCase();
          if (status === 'crashloopbackoff') return 4;
          if (status === 'failed' || status === 'error') return 3;
          if (status === 'imagepullbackoff') return 3;
          if (status === 'pending') return 2;
          if (pod.restart_count > 10) return 1;
          return 0;
        };
        return getPriority(b) - getPriority(a);
      })
      .slice(0, 10);
  };

  // Get deployments with problems (not all replicas ready)
  const getProblematicDeployments = () => {
    return filteredDeployments
      .filter(dep => dep.replicas_ready < dep.replicas_desired)
      .sort((a, b) => {
        // Sort by missing replicas percentage
        const aMissing = (a.replicas_desired - a.replicas_ready) / a.replicas_desired;
        const bMissing = (b.replicas_desired - b.replicas_ready) / b.replicas_desired;
        return bMissing - aMissing;
      })
      .slice(0, 10);
  };

  // Generate smart recommendations based on actual data
  const getSmartRecommendations = () => {
    const smartRecs: Array<{
      id: string;
      severity: 'critical' | 'high' | 'medium' | 'low';
      title: string;
      description: string;
      resource?: string;
      namespace?: string;
    }> = [];

    // Check for pods with high restart counts
    const highRestartPods = filteredPods.filter(p => p.restart_count > 5);
    highRestartPods.slice(0, 3).forEach(pod => {
      smartRecs.push({
        id: `restart-${pod.id}`,
        severity: pod.restart_count > 20 ? 'critical' : 'high',
        title: t('infrastructure:kubernetes.smartRecommendations.podHighRestarts', { name: pod.name, count: pod.restart_count }),
        description: t('infrastructure:kubernetes.smartRecommendations.podHighRestartsDesc'),
        resource: pod.name,
        namespace: typeof pod.namespace === 'object' ? pod.namespace?.name : pod.namespace
      });
    });

    // Check for CrashLoopBackOff pods
    const crashingPods = filteredPods.filter(p => p.status?.toLowerCase() === 'crashloopbackoff');
    crashingPods.slice(0, 3).forEach(pod => {
      smartRecs.push({
        id: `crash-${pod.id}`,
        severity: 'critical',
        title: t('infrastructure:kubernetes.smartRecommendations.podCrashLoop', { name: pod.name }),
        description: t('infrastructure:kubernetes.smartRecommendations.podCrashLoopDesc'),
        resource: pod.name,
        namespace: typeof pod.namespace === 'object' ? pod.namespace?.name : pod.namespace
      });
    });

    // Check for degraded deployments
    const degradedDeps = filteredDeployments.filter(d => d.replicas_ready < d.replicas_desired);
    degradedDeps.slice(0, 3).forEach(dep => {
      const missing = dep.replicas_desired - dep.replicas_ready;
      smartRecs.push({
        id: `deploy-${dep.id}`,
        severity: dep.replicas_ready === 0 ? 'critical' : 'high',
        title: t('infrastructure:kubernetes.smartRecommendations.deploymentDegraded', { name: dep.deployment_name || dep.name }),
        description: t('infrastructure:kubernetes.smartRecommendations.deploymentDegradedDesc', { ready: dep.replicas_ready, desired: dep.replicas_desired, missing }),
        resource: dep.deployment_name || dep.name,
        namespace: typeof dep.namespace === 'object' ? dep.namespace?.name : String(dep.namespace)
      });
    });

    // Check for pending pods
    const pendingPods = filteredPods.filter(p => p.status?.toLowerCase() === 'pending');
    if (pendingPods.length > 0) {
      smartRecs.push({
        id: 'pending-pods',
        severity: pendingPods.length > 5 ? 'high' : 'medium',
        title: t('infrastructure:kubernetes.smartRecommendations.pendingPods', { count: pendingPods.length }),
        description: t('infrastructure:kubernetes.smartRecommendations.pendingPodsDesc'),
        resource: pendingPods.map(p => p.name).slice(0, 3).join(', ')
      });
    }

    // Check for nodes without metrics
    const nodesWithoutMetrics = filteredNodes.filter(n => !n.cpu_usage_percent && !n.memory_usage_percent);
    if (nodesWithoutMetrics.length > 0 && filteredNodes.length > 0) {
      smartRecs.push({
        id: 'no-metrics',
        severity: 'medium',
        title: t('infrastructure:kubernetes.smartRecommendations.noMetrics'),
        description: t('infrastructure:kubernetes.smartRecommendations.noMetricsDesc', { count: nodesWithoutMetrics.length }),
        resource: nodesWithoutMetrics.map(n => n.name).slice(0, 3).join(', ')
      });
    }

    // Check for HPAs at limit
    if (hpasAtLimit.length > 0) {
      hpasAtLimit.slice(0, 2).forEach(hpa => {
        smartRecs.push({
          id: `hpa-${hpa.hpa_name}`,
          severity: 'high',
          title: t('infrastructure:kubernetes.smartRecommendations.hpaAtLimit', { name: hpa.hpa_name }),
          description: t('infrastructure:kubernetes.smartRecommendations.hpaAtLimitDesc', { max: hpa.max_replicas }),
          resource: hpa.hpa_name,
          namespace: hpa.namespace
        });
      });
    }

    return smartRecs;
  };

  const filteredEvents = criticalEvents.filter(event =>
    eventFilter === 'all' || event.severity === eventFilter
  );

  const COLORS = ['#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#3B82F6', '#EC4899'];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">{t('infrastructure:kubernetes.loading')}</div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-red-800 dark:text-red-400">Error: {error}</p>
        </div>
      </MainLayout>
    );
  }

  const filteredNodes = selectedCluster ? nodes.filter(n => n.cluster_id === selectedCluster.id) : nodes;
  const filteredPods = selectedCluster ? pods.filter(p => p.cluster_id === selectedCluster.id) : pods;
  const filteredDeployments = selectedCluster ? deployments.filter(d => d.cluster_id === selectedCluster.id) : deployments;

  // Pagination for each tab
  const totalNodes = filteredNodes.length;
  const paginatedNodes = filteredNodes.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const totalPods = filteredPods.length;
  const paginatedPods = filteredPods.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const totalDeployments = filteredDeployments.length;
  const paginatedDeployments = filteredDeployments.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const totalNamespaces = namespaces.length;
  const paginatedNamespaces = namespaces.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Helper to get current tab totals and paginated data
  const getTabData = () => {
    switch (selectedTab) {
      case 'nodes':
        return { total: totalNodes, data: paginatedNodes };
      case 'pods':
        return { total: totalPods, data: paginatedPods };
      case 'deployments':
        return { total: totalDeployments, data: paginatedDeployments };
      case 'namespaces':
        return { total: totalNamespaces, data: paginatedNamespaces };
      default:
        return { total: 0, data: [] };
    }
  };

  const { total: currentTotal, data: currentData } = getTabData();

  return (
    <MainLayout>
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('infrastructure:kubernetes.title')}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {t('infrastructure:kubernetes.subtitle')}
          </p>
        </div>
        <button
          onClick={() => navigate('/agents/kubernetes/install')}
          className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700"
        >
          <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {t('infrastructure:kubernetes.setupAgent')}
        </button>
      </div>

      {/* Overview/Insights Section */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
          {t('infrastructure:kubernetes.insights.title')}
        </h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {/* Critical Events Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('infrastructure:kubernetes.insights.criticalEvents')}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                  {criticalEvents.length}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  {t('infrastructure:kubernetes.insights.last24h')}
                </p>
              </div>
              <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/20">
                <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <div className="space-y-2">
              {criticalEvents.slice(0, 3).map((event) => (
                <div key={event.id} className="flex items-center space-x-2 text-xs">
                  <span className={`inline-flex rounded-full px-2 py-0.5 font-semibold ${getSeverityColor(event.severity)}`}>
                    {event.reason}
                  </span>
                  <span className="text-gray-500 dark:text-gray-500">{formatTimestamp(event.last_timestamp)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Unhealthy Deployments Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('infrastructure:kubernetes.insights.unhealthyDeployments')}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                  {unhealthyDeployments.length}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  {t('infrastructure:kubernetes.insights.requiresAttention')}
                </p>
              </div>
              <div className="rounded-full bg-yellow-100 p-3 dark:bg-yellow-900/20">
                <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <div className="space-y-2">
              {unhealthyDeployments.map((deployment, idx) => (
                <div key={idx} className="text-xs">
                  <p className="font-medium text-gray-900 dark:text-white">{deployment.deployment_name}</p>
                  <p className="text-gray-500 dark:text-gray-500">
                    {deployment.replicas_ready}/{deployment.replicas_desired} ready - {deployment.issues.join(', ')}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Services Without Endpoints Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('infrastructure:kubernetes.insights.servicesWithoutEndpoints')}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                  {servicesWithoutEndpoints.length}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  {t('infrastructure:kubernetes.insights.noBackendPods')}
                </p>
              </div>
              <div className="rounded-full bg-orange-100 p-3 dark:bg-orange-900/20">
                <svg className="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="space-y-2">
              {servicesWithoutEndpoints.map((service, idx) => (
                <div key={idx} className="text-xs">
                  <p className="font-medium text-gray-900 dark:text-white">{service.service_name}</p>
                  <p className="text-gray-500 dark:text-gray-500">
                    {service.namespace} / {service.service_type}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* HPAs at Limit Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('infrastructure:kubernetes.insights.hpasAtLimit')}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                  {hpasAtLimit.length}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  {t('infrastructure:kubernetes.insights.maxReplicasReached')}
                </p>
              </div>
              <div className="rounded-full bg-purple-100 p-3 dark:bg-purple-900/20">
                <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
            </div>
            <div className="space-y-2">
              {hpasAtLimit.map((hpa, idx) => (
                <div key={idx} className="text-xs">
                  <p className="font-medium text-gray-900 dark:text-white">{hpa.hpa_name}</p>
                  <p className="text-gray-500 dark:text-gray-500">
                    {hpa.current_replicas}/{hpa.max_replicas} replicas - {hpa.namespace}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Resource Health Section */}
      {selectedCluster && (
        <div className="mb-8">
          <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
            {t('infrastructure:kubernetes.resourceHealth.title')}
          </h2>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Pod Status Distribution */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                {t('infrastructure:kubernetes.resourceHealth.podStatus')}
              </h3>
              {getPodStatusData().length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={getPodStatusData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {getPodStatusData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: 'none',
                        borderRadius: '0.5rem',
                        color: '#F9FAFB',
                      }}
                    />
                    <Legend
                      wrapperStyle={{ color: '#9CA3AF' }}
                      formatter={(value) => <span className="text-gray-600 dark:text-gray-300">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[250px] items-center justify-center text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.resourceHealth.noPodsFound')}
                </div>
              )}
            </div>

            {/* Top 5 Namespaces by Pod Count */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                {t('infrastructure:kubernetes.resourceHealth.topNamespaces')}
              </h3>
              {getNamespacePodCounts().length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={getNamespacePodCounts()} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
                    <XAxis type="number" stroke="#9CA3AF" />
                    <YAxis type="category" dataKey="name" stroke="#9CA3AF" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: 'none',
                        borderRadius: '0.5rem',
                        color: '#F9FAFB',
                      }}
                    />
                    <Bar dataKey="count" fill="#6366F1" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[250px] items-center justify-center text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.resourceHealth.noNamespacesWithPods')}
                </div>
              )}
            </div>

            {/* Node Resource Utilization */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                {t('infrastructure:kubernetes.resourceHealth.nodeUtilization')}
              </h3>
              {getNodeResourceUtilization().length > 0 && getNodeResourceUtilization().some(n => n.cpu > 0 || n.memory > 0) ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={getNodeResourceUtilization()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
                    <XAxis dataKey="name" stroke="#9CA3AF" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#9CA3AF" domain={[0, 100]} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: 'none',
                        borderRadius: '0.5rem',
                        color: '#F9FAFB',
                      }}
                      formatter={(value: number) => `${value.toFixed(1)}%`}
                    />
                    <Legend
                      wrapperStyle={{ color: '#9CA3AF' }}
                      formatter={(value) => <span className="text-gray-600 dark:text-gray-300">{value}</span>}
                    />
                    <Bar dataKey="cpu" fill="#10B981" name="CPU %" />
                    <Bar dataKey="memory" fill="#3B82F6" name="Memory %" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[250px] flex-col items-center justify-center text-center">
                  <svg className="mb-2 h-10 w-10 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('infrastructure:kubernetes.resourceHealth.metricsNotAvailable')}</p>
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{t('infrastructure:kubernetes.resourceHealth.metricsHint')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Problematic Pods Section */}
      {selectedCluster && getProblematicPods().length > 0 && (
        <div className="mb-8">
          <h2 className="mb-4 flex items-center text-xl font-semibold text-gray-900 dark:text-white">
            <svg className="mr-2 h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {t('infrastructure:kubernetes.problematicPods.title')} ({getProblematicPods().length})
          </h2>
          <div className="rounded-xl border border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-900/10">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-red-200 bg-red-100/50 dark:border-red-800 dark:bg-red-900/20">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-red-800 dark:text-red-300">{t('infrastructure:kubernetes.problematicPods.pod')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-red-800 dark:text-red-300">{t('infrastructure:kubernetes.problematicPods.namespace')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-red-800 dark:text-red-300">{t('infrastructure:kubernetes.problematicPods.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-red-800 dark:text-red-300">{t('infrastructure:kubernetes.problematicPods.restarts')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-red-800 dark:text-red-300">{t('infrastructure:kubernetes.problematicPods.node')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-200 dark:divide-red-800">
                  {getProblematicPods().map((pod) => (
                    <tr key={pod.id} className="hover:bg-red-100/50 dark:hover:bg-red-900/20">
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                        {pod.name}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {typeof pod.namespace === 'object' ? pod.namespace?.name : pod.namespace || 'N/A'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(pod.status)}`}>
                          {pod.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm">
                        <span className={`font-bold ${pod.restart_count > 10 ? 'text-red-600 dark:text-red-400' : pod.restart_count > 3 ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-900 dark:text-white'}`}>
                          {pod.restart_count}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {pod.node?.name || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Problematic Deployments Section */}
      {selectedCluster && getProblematicDeployments().length > 0 && (
        <div className="mb-8">
          <h2 className="mb-4 flex items-center text-xl font-semibold text-gray-900 dark:text-white">
            <svg className="mr-2 h-6 w-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {t('infrastructure:kubernetes.problematicDeployments.title')} ({getProblematicDeployments().length})
          </h2>
          <div className="rounded-xl border border-yellow-200 bg-yellow-50/50 dark:border-yellow-800 dark:bg-yellow-900/10">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-yellow-200 bg-yellow-100/50 dark:border-yellow-800 dark:bg-yellow-900/20">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-yellow-800 dark:text-yellow-300">{t('infrastructure:kubernetes.problematicDeployments.deployment')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-yellow-800 dark:text-yellow-300">{t('infrastructure:kubernetes.problematicDeployments.namespace')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-yellow-800 dark:text-yellow-300">{t('infrastructure:kubernetes.problematicDeployments.replicas')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-yellow-800 dark:text-yellow-300">{t('infrastructure:kubernetes.problematicDeployments.missing')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-yellow-800 dark:text-yellow-300">{t('infrastructure:kubernetes.problematicDeployments.status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-yellow-200 dark:divide-yellow-800">
                  {getProblematicDeployments().map((dep) => {
                    const missing = dep.replicas_desired - dep.replicas_ready;
                    const percentage = (dep.replicas_ready / dep.replicas_desired) * 100;
                    return (
                      <tr key={dep.id} className="hover:bg-yellow-100/50 dark:hover:bg-yellow-900/20">
                        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                          {dep.deployment_name || dep.name}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                          {typeof dep.namespace === 'object' ? dep.namespace?.name : dep.namespace || 'N/A'}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          <span className="font-medium text-gray-900 dark:text-white">{dep.replicas_ready}</span>
                          <span className="text-gray-500 dark:text-gray-400">/{dep.replicas_desired}</span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          <span className="font-bold text-red-600 dark:text-red-400">-{missing}</span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center">
                            <div className="mr-2 h-2 w-20 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                              <div
                                className={`h-full rounded-full ${percentage === 0 ? 'bg-red-500' : percentage < 50 ? 'bg-orange-500' : 'bg-yellow-500'}`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className={`text-xs font-medium ${percentage === 0 ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                              {percentage.toFixed(0)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Cluster Selector */}
      {clusters.length > 0 ? (
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {clusters.map((cluster) => (
            <div
              key={cluster.id}
              onClick={() => setSelectedCluster(cluster)}
              className={`cursor-pointer rounded-xl border p-6 shadow-sm transition-all hover:shadow-md ${
                selectedCluster?.id === cluster.id
                  ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-500 dark:bg-indigo-900/10'
                  : 'border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900'
              }`}
            >
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{cluster.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{cluster.provider} • {cluster.region || 'N/A'}</p>
                </div>
                <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(cluster.status)}`}>
                  {cluster.status}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-500">{t('infrastructure:kubernetes.nodes')}</p>
                  <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">{cluster.node_count}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-500">{t('infrastructure:kubernetes.pods')}</p>
                  <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">{cluster.pod_count}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400">
          {t('infrastructure:kubernetes.noClustersFound')}
        </div>
      )}

      {/* Resource Usage Chart */}
      {selectedCluster && resourceUsage.length > 0 && (
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t('infrastructure:kubernetes.resourceUtilization')} - {selectedCluster.name}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={resourceUsage} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis type="number" stroke="#9CA3AF" domain={[0, 100]} />
              <YAxis type="category" dataKey="resource" stroke="#9CA3AF" width={150} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB',
                }}
              />
              <Bar dataKey="used" fill="#6366F1" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {['nodes', 'pods', 'deployments', 'namespaces'].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab as any)}
              className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium ${
                selectedTab === tab
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t(`infrastructure:kubernetes.${tab}`)}
            </button>
          ))}
        </nav>
      </div>

      {/* Nodes Tab */}
      {selectedTab === 'nodes' && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.cpu')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.memory')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.pods')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.cpuCapacity')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {paginatedNodes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    {t('infrastructure:kubernetes.noNodesFound')}
                  </td>
                </tr>
              ) : (
                paginatedNodes.map((node) => (
                  <tr
                    key={node.id}
                    onClick={() => node.host_id && navigate(`/infrastructure/hosts/${node.host_id}`)}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 ${node.host_id ? 'cursor-pointer' : ''}`}
                  >
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {node.name}
                      {node.host_id && (
                        <span className="ml-2 text-xs text-gray-500">
                          🔗
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(node.status)}`}>
                        {node.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {node.cpu_usage_percent?.toFixed(1)}%
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {node.memory_usage_percent?.toFixed(1)}%
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {node.pod_count} / {node.pods_capacity || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {node.cpu_capacity || 'N/A'} CPU
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pods Tab */}
      {selectedTab === 'pods' && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.namespace')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.restarts')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.node')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.created')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {paginatedPods.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    {t('infrastructure:kubernetes.noPodsFound')}
                  </td>
                </tr>
              ) : (
                paginatedPods.map((pod) => (
                  <tr key={pod.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {pod.name}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {pod.namespace?.name || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(pod.status)}`}>
                        {pod.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {pod.restart_count}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {pod.node?.name || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {new Date(pod.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Deployments Tab */}
      {selectedTab === 'deployments' && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.namespace')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.replicas')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.ready')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.upToDate')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.available')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {paginatedDeployments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    {t('infrastructure:kubernetes.noDeploymentsFound')}
                  </td>
                </tr>
              ) : (
                paginatedDeployments.map((deployment) => (
                  <tr key={deployment.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {deployment.deployment_name || deployment.name}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {deployment.namespace?.name || deployment.namespace || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {deployment.replicas_desired}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {deployment.replicas_ready}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {deployment.replicas_up_to_date}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {deployment.replicas_available}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Namespaces Tab */}
      {selectedTab === 'namespaces' && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.pods')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('infrastructure:kubernetes.created')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {paginatedNamespaces.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    {t('infrastructure:kubernetes.noNamespacesFound')}
                  </td>
                </tr>
              ) : (
                paginatedNamespaces.map((namespace) => (
                  <tr key={namespace.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {namespace.name}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(namespace.status)}`}>
                        {namespace.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {namespace.pod_count}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {new Date(namespace.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Events Timeline Section */}
      <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('infrastructure:kubernetes.eventsTimeline.title')}
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setEventFilter('all')}
              className={`rounded-lg px-3 py-1 text-sm font-medium transition-colors ${
                eventFilter === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {t('infrastructure:kubernetes.eventsTimeline.all')}
            </button>
            <button
              onClick={() => setEventFilter('error')}
              className={`rounded-lg px-3 py-1 text-sm font-medium transition-colors ${
                eventFilter === 'error'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {t('infrastructure:kubernetes.eventsTimeline.errors')}
            </button>
            <button
              onClick={() => setEventFilter('warning')}
              className={`rounded-lg px-3 py-1 text-sm font-medium transition-colors ${
                eventFilter === 'warning'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {t('infrastructure:kubernetes.eventsTimeline.warnings')}
            </button>
          </div>
        </div>
        <div className="space-y-3">
          {filteredEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <svg className="mb-3 h-12 w-12 text-gray-400 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400">
                {t('infrastructure:kubernetes.eventsTimeline.noEvents')}
              </p>
              <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
                {t('infrastructure:kubernetes.eventsTimeline.noEventsHint')}
              </p>
            </div>
          ) : (
            filteredEvents.map((event) => (
              <div
                key={event.id}
                className="flex items-start space-x-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700"
              >
                <div className={`mt-1 h-3 w-3 flex-shrink-0 rounded-full ${
                  event.severity === 'error' ? 'bg-red-500' : event.severity === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
                }`} />
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{event.message}</p>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {t('infrastructure:kubernetes.eventsTimeline.resource')}: {event.object_kind ? `${event.object_kind}/${event.object_name}` : event.object_name || event.namespace}
                      </p>
                    </div>
                    <span className={`ml-4 inline-flex flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${getSeverityColor(event.severity)}`}>
                      {event.reason}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                    {formatTimestamp(event.last_timestamp)}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Smart Recommendations Section - based on actual cluster data */}
      {selectedCluster && getSmartRecommendations().length > 0 && (
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <h2 className="mb-2 flex items-center text-xl font-semibold text-gray-900 dark:text-white">
            <svg className="mr-2 h-6 w-6 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            {t('infrastructure:kubernetes.smartRecommendations.title')} ({getSmartRecommendations().length})
          </h2>
          <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
            {t('infrastructure:kubernetes.smartRecommendations.subtitle')}
          </p>
          <div className="space-y-4">
            {getSmartRecommendations().map((rec) => (
              <div
                key={rec.id}
                className={`rounded-lg border-l-4 p-4 ${getRecommendationColor(rec.severity)}`}
              >
                <div className="flex items-start space-x-3">
                  {getRecommendationIcon(rec.severity)}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {rec.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                      {rec.description}
                    </p>
                    {(rec.resource || rec.namespace) && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {rec.namespace && (
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                            📁 {rec.namespace}
                          </span>
                        )}
                        {rec.resource && (
                          <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
                            🔧 {rec.resource}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Clear Message - when no problems found */}
      {selectedCluster && getSmartRecommendations().length === 0 && getProblematicPods().length === 0 && getProblematicDeployments().length === 0 && (
        <div className="mb-8 rounded-xl border border-green-200 bg-green-50 p-6 dark:border-green-800 dark:bg-green-900/10">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-300">{t('infrastructure:kubernetes.clusterHealthy.title')}</h3>
              <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                {t('infrastructure:kubernetes.clusterHealthy.description')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {currentData.length > 0 && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {t('infrastructure:pagination.showing')} {((currentPage - 1) * pageSize) + 1} {t('infrastructure:pagination.to')} {Math.min(currentPage * pageSize, currentTotal)} {t('infrastructure:pagination.of')} {currentTotal} {t(`infrastructure:kubernetes.${selectedTab}`)}
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="ml-4 rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
              >
                <option value={10}>10 {t('infrastructure:pagination.perPage')}</option>
                <option value={20}>20 {t('infrastructure:pagination.perPage')}</option>
                <option value={50}>50 {t('infrastructure:pagination.perPage')}</option>
              </select>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('infrastructure:pagination.first')}
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('infrastructure:pagination.previous')}
              </button>
              <span className="flex items-center px-3 text-sm text-gray-700 dark:text-gray-300">
                {t('infrastructure:pagination.page')} {currentPage} {t('infrastructure:pagination.of')} {Math.ceil(currentTotal / pageSize)}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= Math.ceil(currentTotal / pageSize)}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('infrastructure:pagination.next')}
              </button>
              <button
                onClick={() => setCurrentPage(Math.ceil(currentTotal / pageSize))}
                disabled={currentPage >= Math.ceil(currentTotal / pageSize)}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('infrastructure:pagination.last')}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
